import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import './TimerTab.mobile.css';
// Audio file paths:
// - Single ding: /assets/ding.mp3
// - Repeating bell: /assets/repeat-bell.mp3

interface TimerTabProps {
  format: string; // 'BP', 'AP', or 'PDA'
}

const TIMER_CONFIG = {
  BP: { green: 60, orange: 360, red: 420 },
  AP: { green: 60, orange: 360, red: 420 },
  PDA: { green: 60, orange: 180, red: 210 },
};

const getTimerConfig = (format: string) => {
  if (format === 'PDA') return TIMER_CONFIG.PDA;
  return TIMER_CONFIG.BP; // BP and AP share config
};

const TimerTab: React.FC<TimerTabProps> = ({ format }) => {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [flash, setFlash] = useState<'none' | 'green' | 'orange' | 'red'>('none');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const repeatBellRef = useRef<HTMLAudioElement | null>(null);
  const [repeatBellActive, setRepeatBellActive] = useState(false);

  const { green, orange, red } = getTimerConfig(format);

  // Play bell sound manually, allow spam/instant play
  const handleBell = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running]);

  // Handle color flashes and single dings
  useEffect(() => {
    if (!running) return;
    if (seconds === green) {
      setFlash('green');
      audioRef.current?.play();
      setTimeout(() => setFlash('none'), 1000);
    } else if (seconds === orange) {
      setFlash('orange');
      audioRef.current?.play();
      setTimeout(() => setFlash('none'), 1000);
    } else if (seconds === red) {
      setFlash('red');
      setRepeatBellActive(true);
    }
  }, [seconds, running, green, orange, red]);

  // Play/stop repeat bell at end threshold
  useEffect(() => {
    if (repeatBellActive && running) {
      repeatBellRef.current?.currentTime && (repeatBellRef.current.currentTime = 0);
      repeatBellRef.current?.play();
    } else {
      repeatBellRef.current?.pause();
      repeatBellRef.current && (repeatBellRef.current.currentTime = 0);
    }
  }, [repeatBellActive, running]);

  // Stop repeat bell if timer is paused or reset
  useEffect(() => {
    if (!running) setRepeatBellActive(false);
  }, [running]);

  const handleStart = () => setRunning(true);
  const handlePause = () => setRunning(false);
  const handleReset = () => {
    setRunning(false);
    setSeconds(0);
    setFlash('none');
    setRepeatBellActive(false);
  };

  const pad = (n: number) => n.toString().padStart(2, '0');
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;

  let flashClass = '';
  if (flash === 'green') flashClass = 'animate-flash-green';
  if (flash === 'orange') flashClass = 'animate-flash-orange';
  if (flash === 'red') flashClass = 'animate-flash-red';

  return (
    <div className={`flex flex-col items-center justify-center p-6 ${flashClass} w-full`}>
      <audio ref={audioRef} src="/assets/ding.mp3" preload="auto" />
      <audio ref={repeatBellRef} src="/assets/repeat-bell.mp3" preload="auto" />
      <div className="text-5xl font-mono mb-4 select-none w-full text-center break-words">
        {pad(min)}:{pad(sec)}
      </div>
      <div className="flex flex-wrap gap-2 mb-4 w-full justify-center">
        {!running ? (
          <button onClick={handleStart} className="px-4 py-2 bg-green-600 text-white rounded w-24 sm:w-auto">Start</button>
        ) : (
          <button onClick={handlePause} className="px-4 py-2 bg-yellow-500 text-white rounded w-24 sm:w-auto">Pause</button>
        )}
        <button onClick={handleReset} className="px-4 py-2 bg-gray-500 text-white rounded w-24 sm:w-auto">Reset</button>
        <button onClick={handleBell} className="px-4 py-2 bg-blue-500 text-white rounded w-24 sm:w-auto" title="Ring Bell"><Bell size={20} /></button>
      </div>
      <div className="text-xs text-gray-400 w-full text-center">
        {format === 'PDA' ? 'PDA Parliamentary Timer' : 'BP/AP Shared Timer'}
      </div>
      {/* Flashing styles */}
      <style>{`
        .animate-flash-green { background: #22c55e33; transition: background 0.2s; }
        .animate-flash-orange { background: #f59e4233; transition: background 0.2s; }
        .animate-flash-red { background: #ef444433; transition: background 0.2s; }
      `}</style>
    </div>
  );
};

export default TimerTab; 